(() => {
  const initSection = (section) => {
    const form = section.querySelector('[data-custom-slipcover-form]');
    if (!form || form.dataset.initialized === 'true') return;

    form.dataset.initialized = 'true';

    const panels = Array.from(form.querySelectorAll('[data-form-step]'));
    const progressItems = Array.from(form.querySelectorAll('[data-progress-step]'));
    const status = form.querySelector('[data-form-status]');
    const photoStatus = form.querySelector('[data-photo-status]');
    const photoLink = form.querySelector('[data-photo-link]');
    const photoInputs = Array.from(form.querySelectorAll('[data-photo-input]'));
    const review = form.querySelector('[data-form-review]');
    const submitButton = form.querySelector('[data-form-submit]');
    const successTemplate = form.querySelector('[data-form-success-template]');
    const totalWidthInput = form.querySelector('[data-quote-total-width]');
    const totalWidthEmail = form.querySelector('[data-quote-total-width-email]');
    const measurementUnitInput = form.querySelector('[data-quote-measurement-unit]');
    let activeStep = 1;
    let submissionWatchdog = null;
    let submissionPending = false;

    const draftStorageKey = `custom-slipcover-draft:${window.location.pathname}:${form.id || 'quote'}`;
    const readDraft = () => {
      try {
        const value = window.sessionStorage.getItem(draftStorageKey);
        return value ? JSON.parse(value) : null;
      } catch (error) {
        return null;
      }
    };
    const storedDraft = readDraft();

    const contactPosted = new URLSearchParams(window.location.search).get('contact_posted') === 'true';
    if (contactPosted && successTemplate) {
      try {
        window.sessionStorage.removeItem(draftStorageKey);
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
      form.replaceChildren(successTemplate.content.cloneNode(true));
      requestAnimationFrame(() => {
        form.querySelector('.custom-slipcovers__form-message--success')?.focus();
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    const externalEndpoint = () => form.dataset.externalEndpoint?.trim() || '';

    const fieldValue = (name) => {
      const field = form.elements.namedItem(name);
      if (!field) return '';
      return typeof field.value === 'string' ? field.value.trim() : '';
    };

    const selectedFiles = () =>
      photoInputs
        .filter((input) => input.files && input.files.length)
        .map((input) => input.files[0]);

    const saveDraft = (step = activeStep) => {
      const values = {};

      Array.from(form.elements).forEach((field) => {
        if (
          !field.name ||
          field.hasAttribute('data-skip-draft') ||
          field.type === 'file' ||
          field.type === 'submit' ||
          field.type === 'button'
        ) return;

        if (field.type === 'checkbox' || field.type === 'radio') {
          values[field.name] = { checked: field.checked };
          return;
        }

        values[field.name] = { value: field.value };
      });

      try {
        window.sessionStorage.setItem(
          draftStorageKey,
          JSON.stringify({
            step,
            values,
            hadLocalFiles: selectedFiles().length > 0,
            submissionPending,
            savedAt: Date.now(),
          }),
        );
      } catch (error) {
        // Continue normally if session storage is unavailable or full.
      }
    };

    const restoreDraft = () => {
      if (!storedDraft?.values) return false;

      Object.entries(storedDraft.values).forEach(([name, saved]) => {
        const field = form.elements.namedItem(name);
        if (!field || !('type' in field)) return;

        if (field.type === 'checkbox' || field.type === 'radio') {
          field.checked = Boolean(saved.checked);
        } else if (typeof saved.value === 'string') {
          field.value = saved.value;
        }
      });

      submissionPending = Boolean(storedDraft.submissionPending);
      return true;
    };

    const clearDraft = () => {
      submissionPending = false;
      try {
        window.sessionStorage.removeItem(draftStorageKey);
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
    };

    const setStatus = (message, isError = false) => {
      if (!status) return;
      status.textContent = message;
      status.style.color = isError ? '#a93030' : '';
    };

    const setPhotoStatus = (message = '', isError = false) => {
      if (!photoStatus) return;
      photoStatus.textContent = message;
      photoStatus.style.color = isError ? '#a93030' : '';
    };

    const updateReview = () => {
      if (!review) return;

      const files = selectedFiles();
      const shareLink = photoLink?.value.trim() || '';
      let photoSummary = shareLink ? 'Share link provided' : 'Not provided';

      if (files.length) {
        photoSummary = `${files.length} file${files.length === 1 ? '' : 's'} selected`;
      }

      const rows = [
        ['Brand', fieldValue('contact[furniture_brand]') || 'Not provided'],
        ['Model', fieldValue('contact[furniture_model]') || 'Not provided'],
        ['Furniture', fieldValue('contact[furniture_type]') || 'Not provided'],
        [
          'Approx. width',
          `${fieldValue('quote_total_width') || '—'} ${fieldValue('quote_measurement_unit') || ''}`.trim(),
        ],
        ['Name', fieldValue('contact[name]') || 'Not provided'],
        ['Email', fieldValue('contact[email]') || 'Not provided'],
        ['Photos', photoSummary],
        ['Country', fieldValue('contact[country]') || 'Not provided'],
      ];

      review.replaceChildren(
        ...rows.map(([label, value]) => {
          const item = document.createElement('div');
          item.className = 'custom-slipcovers__review-item';

          const itemLabel = document.createElement('span');
          itemLabel.textContent = label;

          const itemValue = document.createElement('strong');
          itemValue.textContent = value;

          item.append(itemLabel, itemValue);
          return item;
        }),
      );
    };

    const setStep = (nextStep, options = {}) => {
      const { focus = true, scroll = true, clearStatus = true, persist = true } = options;
      activeStep = Math.max(1, Math.min(nextStep, panels.length));

      panels.forEach((panel) => {
        const panelStep = Number(panel.dataset.formStep);
        const isActive = panelStep === activeStep;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

      progressItems.forEach((item) => {
        const itemStep = Number(item.dataset.progressStep);
        item.classList.toggle('is-active', itemStep === activeStep);
        item.classList.toggle('is-complete', itemStep < activeStep);
        if (itemStep === activeStep) {
          item.setAttribute('aria-current', 'step');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      if (activeStep === panels.length) updateReview();

      const heading = panels[activeStep - 1]?.querySelector('h3');
      if (focus && heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }

      if (scroll) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (clearStatus) setStatus('');
      if (persist) saveDraft(activeStep);
    };

    const visibleRequiredFields = () =>
      Array.from(panels[activeStep - 1]?.querySelectorAll('[required]') || []);

    const validatePhotos = () => {
      if (activeStep !== 3) return true;

      const files = selectedFiles();
      const shareLink = photoLink?.value.trim() || '';

      // Shopify's native contact form does not deliver file attachments. When
      // no external upload endpoint is configured, require a public share link
      // so the request can still be delivered without losing the form state.
      if (!externalEndpoint()) {
        if (shareLink) {
          setPhotoStatus('');
          return true;
        }

        const nativeMessage = files.length
          ? 'Your photos are selected. Please also add a public Google Drive, Dropbox, iCloud or other share link so we can receive them.'
          : 'Please add a public Google Drive, Dropbox, iCloud or other photo share link.';

        setPhotoStatus(nativeMessage, true);
        photoLink?.focus();
        return false;
      }

      if (files.length || shareLink) {
        setPhotoStatus('');
        return true;
      }

      const message = 'Please upload at least one clear photo or add a public photo share link.';

      setPhotoStatus(message, true);
      photoLink?.focus();
      return false;
    };

    const validateStep = () => {
      const fields = visibleRequiredFields();

      for (const field of fields) {
        field.setAttribute('aria-invalid', field.checkValidity() ? 'false' : 'true');
        if (!field.checkValidity()) {
          field.reportValidity();
          field.focus();
          return false;
        }
      }

      return validatePhotos();
    };

    form.addEventListener('click', (event) => {
      const nextButton = event.target.closest('[data-form-next]');
      const backButton = event.target.closest('[data-form-back]');

      if (nextButton && validateStep()) setStep(activeStep + 1);
      if (backButton) setStep(activeStep - 1);
    });

    form.querySelectorAll('[required]').forEach((field) => {
      field.addEventListener('input', () => {
        if (field.checkValidity()) field.removeAttribute('aria-invalid');
      });
      field.addEventListener('change', () => {
        if (field.checkValidity()) field.removeAttribute('aria-invalid');
      });
    });

    photoLink?.addEventListener('input', () => setPhotoStatus(''));

    form.addEventListener('input', () => saveDraft(activeStep));
    form.addEventListener('change', () => saveDraft(activeStep));

    photoInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const upload = input.closest('.custom-slipcovers__upload');
        const fileName = upload?.querySelector('[data-file-name]');
        const file = input.files && input.files[0];

        upload?.classList.toggle('has-file', Boolean(file));
        if (fileName) fileName.textContent = file ? file.name : 'No file selected';
        if (file) setPhotoStatus('');
      });
    });

    form.addEventListener('submit', async (event) => {
      if (activeStep !== panels.length) {
        event.preventDefault();
        if (validateStep()) setStep(activeStep + 1);
        return;
      }

      if (!form.checkValidity()) return;

      // Shopify sends contact properties as separate email rows. Keep the
      // visible number field numeric, then prepare the email-only value so
      // the notification reads, for example, "82.5 in" in one row.
      if (totalWidthEmail) {
        const width = totalWidthInput?.value.trim() || '';
        const unit = measurementUnitInput?.value.trim() || '';
        totalWidthEmail.value = width && unit ? `${width} ${unit}` : width;
      }

      const submittedAt = form.querySelector('[data-quote-submitted-at]');
      if (submittedAt) submittedAt.value = new Date().toISOString();

      const endpoint = externalEndpoint();
      if (!endpoint) {
        // Exclude local files from Shopify's native contact request. Sending
        // multipart file fields causes Shopify's security flow to return to the
        // first step without creating a contact notification. The public photo
        // link remains part of the email payload.
        photoInputs.forEach((input) => {
          input.disabled = true;
        });
        submissionPending = true;
        saveDraft(panels.length);
        submitButton?.setAttribute('aria-busy', 'true');
        setStatus('Completing the security check and sending your request...');
        window.clearTimeout(submissionWatchdog);
        submissionWatchdog = window.setTimeout(() => {
          photoInputs.forEach((input) => {
            input.disabled = false;
          });
          setStatus(
            'Your request has not been confirmed yet. Please complete the security check, then press Send my quote request again.',
            true,
          );
          submitButton?.removeAttribute('aria-busy');
        }, 30000);
        return;
      }

      event.preventDefault();
      submitButton?.setAttribute('disabled', 'disabled');
      submitButton?.setAttribute('aria-busy', 'true');
      setStatus('Uploading your details and photos…');

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: new FormData(form),
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const successMessage = successTemplate?.content.cloneNode(true);
        if (successMessage) {
          clearDraft();
          form.replaceChildren(successMessage);
        }
        form.querySelector('.custom-slipcovers__form-message')?.focus();
      } catch (error) {
        setStatus('We could not send the request. Please try again or contact us directly.', true);
        submitButton?.removeAttribute('disabled');
        submitButton?.removeAttribute('aria-busy');
      }
    });

    if (restoreDraft()) {
      const restoredStep = submissionPending ? panels.length : Number(storedDraft.step) || 1;
      setStep(restoredStep, { focus: false, scroll: false, clearStatus: false, persist: false });

      if (submissionPending) {
        setStatus(
          storedDraft.hadLocalFiles
            ? 'Your previous submission was not confirmed. Your details and photo link were restored at the final step. Local file selections cannot be restored by the browser, so please use the public photo link and send again.'
            : 'Your previous submission was not confirmed. Your details were restored at the final step. Please complete the security check and send again.',
          true,
        );
      }
    }
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-custom-slipcovers]').forEach(initSection);
    if (root.matches?.('[data-custom-slipcovers]')) initSection(root);
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
